import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  LogOut, 
  Package, 
  Search,
  X,
  Save,
  Upload,
  ChevronLeft,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    name_de: '',
    description_ar: '',
    description_en: '',
    description_de: '',
    price: '',
    category: '',
    image_url: '',
    is_available: true,
    sort_order: 0
  });

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/products`),
        fetch(`${API_URL}/api/categories`)
      ]);
      
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data);
      }
      
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      name_ar: '',
      name_en: '',
      name_de: '',
      description_ar: '',
      description_en: '',
      description_de: '',
      price: '',
      category: categories[0]?.name_ar || '',
      image_url: '',
      is_available: true,
      sort_order: products.length + 1
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name_ar: product.name_ar,
      name_en: product.name_en,
      name_de: product.name_de || '',
      description_ar: product.description_ar,
      description_en: product.description_en,
      description_de: product.description_de || '',
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url || '',
      is_available: product.is_available,
      sort_order: product.sort_order
    });
    setShowModal(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    if (!formData.name_ar || !formData.name_en || !formData.price) {
      setError('الرجاء ملء جميع الحقول المطلوبة');
      return;
    }

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      sort_order: parseInt(formData.sort_order) || 0
    };

    try {
      const url = editingProduct 
        ? `${API_URL}/api/admin/products/${editingProduct.id}`
        : `${API_URL}/api/admin/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowModal(false);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.detail || 'حدث خطأ');
      }
    } catch (err) {
      setError('خطأ في الاتصال بالخادم');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name_en.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <div className="text-[#0d4023] text-xl">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]" dir="rtl">
      {/* Header */}
      <header className="bg-[#0d4023] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-to-home"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-[#b89759]" />
                <h1 className="text-xl font-bold">إدارة المنتجات</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/settings')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="go-to-settings"
              >
                <Settings className="w-4 h-4" />
                <span>الإعدادات</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5a7062]" />
            <input
              type="text"
              placeholder="بحث عن منتج..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none bg-white"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-6 py-3 bg-[#b89759] text-[#0d4023] rounded-xl font-semibold hover:bg-[#cba660] transition-colors"
            data-testid="add-product-btn"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة منتج</span>
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl border border-[#b89759]/10 overflow-hidden hover:border-[#b89759]/30 transition-colors"
              data-testid={`product-card-${product.id}`}
            >
              {/* Product Image */}
              <div className="relative aspect-[4/3]">
                <img
                  src={product.image_url || 'https://via.placeholder.com/400x300'}
                  alt={product.name_ar}
                  className="w-full h-full object-cover"
                />
                {!product.is_available && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                      غير متوفر
                    </span>
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-[#0d4023] text-white px-3 py-1 rounded-full text-sm font-bold">
                  €{product.price.toFixed(2)}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-5">
                <h3 className="font-bold text-[#0d4023] text-lg mb-1">{product.name_ar}</h3>
                <p className="text-[#5a7062] text-sm mb-3">{product.name_en}</p>
                <p className="text-[#5a7062] text-sm line-clamp-2 mb-4">{product.description_ar}</p>
                
                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(product)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#0d4023]/5 text-[#0d4023] rounded-lg hover:bg-[#0d4023]/10 transition-colors"
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                    <span>تعديل</span>
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-[#b89759]/30 mb-4" />
            <p className="text-[#5a7062]">لا توجد منتجات</p>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#b89759]/10 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0d4023]">
                {editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-[#0d4023]/5 rounded-lg transition-colors"
                data-testid="close-modal"
              >
                <X className="w-5 h-5 text-[#5a7062]" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Arabic Name */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  اسم المنتج (عربي) *
                </label>
                <input
                  type="text"
                  value={formData.name_ar}
                  onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  required
                  data-testid="input-name-ar"
                />
              </div>

              {/* English Name */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  اسم المنتج (إنجليزي) *
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  required
                  data-testid="input-name-en"
                />
              </div>

              {/* German Name */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  اسم المنتج (ألماني)
                </label>
                <input
                  type="text"
                  value={formData.name_de}
                  onChange={(e) => setFormData({...formData, name_de: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  data-testid="input-name-de"
                />
              </div>

              {/* Arabic Description */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  الوصف (عربي) *
                </label>
                <textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  required
                  data-testid="input-desc-ar"
                />
              </div>

              {/* English Description */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  الوصف (إنجليزي) *
                </label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  dir="ltr"
                  required
                  data-testid="input-desc-en"
                />
              </div>

              {/* German Description */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  الوصف (ألماني)
                </label>
                <textarea
                  value={formData.description_de}
                  onChange={(e) => setFormData({...formData, description_de: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  dir="ltr"
                  data-testid="input-desc-de"
                />
              </div>

              {/* Price & Category Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    السعر (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    required
                    data-testid="input-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    التصنيف
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none bg-white"
                    data-testid="input-category"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name_ar}>{cat.name_ar}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">
                  رابط الصورة
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  placeholder="https://..."
                  data-testid="input-image-url"
                />
                {formData.image_url && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-[#b89759]/10">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              {/* Availability & Sort Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    الترتيب
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    data-testid="input-sort-order"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_available}
                      onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
                      className="w-5 h-5 rounded border-[#b89759]/20 text-[#0d4023] focus:ring-[#b89759]"
                      data-testid="input-available"
                    />
                    <span className="text-sm font-medium text-[#0d4023]">متوفر للبيع</span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-[#b89759]/20 text-[#5a7062] rounded-xl font-medium hover:bg-[#0d4023]/5 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#0d4023] text-white rounded-xl font-semibold hover:bg-[#1a5c32] transition-colors"
                  data-testid="submit-product"
                >
                  <Save className="w-5 h-5" />
                  <span>{editingProduct ? 'حفظ التعديلات' : 'إضافة المنتج'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
